import React, { forwardRef } from 'react';

import {
  DealerSeachFilterKey,
  IDealerSearchFilter,
} from '@/components/sections/dealer';
import { Button } from '@/components/shared/buttons/button';
import { Checkbox } from '@/components/shared/form/checkbox';
import { Radio } from '@/components/shared/form/radio';
import { Select } from '@/components/shared/form/select';
import { TextField } from '@/components/shared/form/text-field';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import { Flex } from '@/components/shared/layout/flex';
import { Grid } from '@/components/shared/layout/grid';
import { Section } from '@/components/shared/layout/section';
import { styled } from '@/stitches.config';
import { IDistrict } from '@/types/dealer/district';
import { IProvince } from '@/types/dealer/province';

const DealerFilterItem = styled('div', {
  position: 'relative',
  '@maxlg': {
    flex: '0 0 100%',
    maxWidth: '100%',
    borderBottom: '1px solid $gray400',
    paddingBottom: '$space-4',
    marginBottom: '$space-4',
  },
  '&:not(:last-child)': {
    '@lg': {
      paddingRight: '$space-6',
      marginRight: '$space-6',
      borderRight: '1px solid transparent',
      '@xl': {
        paddingRight: '$space-7',
        marginRight: '$space-7',
      },
    },
    '&::after': {
      '@lg': {
        content: '""',
        position: 'absolute',
        width: 1,
        height: 26,
        top: 'calc(50% - 13px)',
        left: '100%',
        bc: '$gray400',
      },
    },
  },
});

interface DealerSearchFilterProps {
  searchFilter: IDealerSearchFilter;
  provinceID: string;
  districtID: string;
  provinces: IProvince[];
  districts: IDistrict[];
  searchValue: string;
  isLoadingDealer: boolean;
  onProvinceChange: (id: string) => void;
  onDistrictChange: (id: string) => void;
  onSearchChange: (value: string) => void;
  onHandleChange: (name: DealerSeachFilterKey, value: any) => void;
  onSubmit: () => void;
}

export const DealerSearchFilter = forwardRef<
  HTMLDivElement,
  DealerSearchFilterProps
>((props, ref) => {
  const getSortedProvince = (provinces: IProvince[]) => {
    return provinces.sort((a, b) => a.name_th.localeCompare(b.name_th, 'th'));
  };

  const getSortedDistrict = (districts: IDistrict[]) => {
    return districts.sort((a, b) => a.name_th.localeCompare(b.name_th, 'th'));
  };

  const onProvinceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    props.onProvinceChange && props.onProvinceChange(e.target.value);
  };

  const onDistrictChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    props.onDistrictChange && props.onDistrictChange(e.target.value);
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^ก-๏a-zA-Z0-9\(\)\s]/g, '');
    props.onSearchChange && props.onSearchChange(value);
  };

  const handleChange = (name: DealerSeachFilterKey | string, value: any) => {
    props.onHandleChange(name as DealerSeachFilterKey, value);
  };

  return (
    <Section
      ref={ref}
      as="div"
      size="sm"
      backgroundColor={{ '@initial': 'gray-light', '@lg': 'white' }}
      test="dealer_filter"
    >
      <Container>
        <Grid rowGap={{ '@initial': 4, '@lg': 6 }}>
          <Flex wrap="wrap" gap="4">
            <Box
              css={{
                flex: '0 0 100%',
                maxWidth: '100%',
                '@lg': {
                  flex: '1 1 0%',
                },
              }}
            >
              <Select
                name="province"
                value={props.provinceID}
                disabled={props.isLoadingDealer}
                onChange={onProvinceChange}
              >
                <option value="">ทุกจังหวัด</option>
                {getSortedProvince(props.provinces).map((o) => (
                  <option key={o.uid} value={o.uid}>
                    {o.name_th}
                  </option>
                ))}
              </Select>
            </Box>
            <Box
              css={{
                flex: '0 0 100%',
                maxWidth: '100%',
                '@lg': {
                  flex: '1 1 0%',
                },
              }}
            >
              <Select
                name="district"
                value={props.districtID}
                disabled={props.isLoadingDealer}
                onChange={onDistrictChange}
              >
                <option value="">ทุกเขต/อำเภอ</option>
                {getSortedDistrict(props.districts).map((o) => (
                  <option key={o.uid} value={o.uid}>
                    {o.name_th}
                  </option>
                ))}
              </Select>
            </Box>
            <Box
              css={{
                flex: '0 0 100%',
                maxWidth: '100%',
                '@lg': {
                  flex: '1 1 0%',
                },
              }}
            >
              <TextField
                name="search_dealer"
                type="text"
                placeholder="ค้นหาจากชื่อโชว์รูม / ศูนย์บริการ"
                disabled={props.isLoadingDealer}
                value={props.searchValue}
                onChange={onSearchChange}
                icon="search"
              />
            </Box>
          </Flex>
          <Flex
            wrap={{ '@initial': 'wrap', '@lg': 'nowrap' }}
            alignItems={{ '@lg': 'end' }}
            justifyContent={{ '@lg': 'between' }}
            rowGap="4"
            columnGap={{ '@initial': 2, '@lg': 4 }}
          >
            <Flex
              wrap="wrap"
              css={{
                '@maxlg': {
                  flex: '0 0 100%',
                  maxWidth: '100%',
                },
              }}
            >
              <DealerFilterItem>
                <Radio
                  name={DealerSeachFilterKey.VehicleType}
                  disabled={props.isLoadingDealer}
                  items={[
                    {
                      value: 'lcv',
                      label: 'รถปิกอัพและรถอเนกประสงค์',
                      checked:
                        props.searchFilter[DealerSeachFilterKey.VehicleType] ===
                        'lcv',
                    },
                    {
                      value: 'cv',
                      label: 'รถบรรทุก',
                      checked:
                        props.searchFilter[DealerSeachFilterKey.VehicleType] ===
                        'cv',
                    },
                  ]}
                  onChangeInput={handleChange}
                />
              </DealerFilterItem>
              <Box
                css={{
                  '@lg': {
                    display: 'flex',
                  },
                  '@maxlg': {
                    flex: '0 0 100%',
                    maxWidth: '100%',
                  },
                }}
              >
                <DealerFilterItem>
                  {/* eslint-disable-next-line react/jsx-max-depth */}
                  <Checkbox
                    name="location"
                    disabled={props.isLoadingDealer}
                    items={[
                      {
                        name: DealerSeachFilterKey.Showroom,
                        label: 'โชว์รูม',
                        checked:
                          props.searchFilter[DealerSeachFilterKey.Showroom],
                      },
                      {
                        name: DealerSeachFilterKey.Service,
                        label: 'ศูนย์บริการ',
                        checked:
                          props.searchFilter[DealerSeachFilterKey.Service],
                      },
                    ]}
                    onChangeInput={handleChange}
                  />
                </DealerFilterItem>
                {!!props.searchFilter[DealerSeachFilterKey.Service] && (
                  <DealerFilterItem data-test="services_available">
                    <Checkbox
                      name="available"
                      disabled={props.isLoadingDealer}
                      items={[
                        {
                          name: DealerSeachFilterKey.BodyPaint,
                          label: 'ซ่อมสีและตัวถัง',
                        },
                        {
                          name: DealerSeachFilterKey.ChangeTire,
                          label: 'เปลี่ยนยางและตั้งศูนย์ถ่วงล้อ',
                        },
                        {
                          name: DealerSeachFilterKey.Overhaul,
                          label: 'โอเวอร์ฮอล์',
                        },
                      ]}
                      onChangeInput={handleChange}
                    />
                  </DealerFilterItem>
                )}
              </Box>
            </Flex>
            <Box
              css={{
                '@maxlg': {
                  flex: '0 0 100%',
                  maxWidth: '100%',
                  textAlign: 'center',
                },
              }}
            >
              <Button
                label="ค้นหา"
                isLoading={props.isLoadingDealer}
                tracking={{
                  dataTrack: 'dealer_search',
                  dataTrackText: 'ค้นหา',
                  dataTrackUrl: '',
                }}
                test={{
                  dataTest: 'button_search',
                }}
                onClick={props.onSubmit}
              />
            </Box>
          </Flex>
        </Grid>
      </Container>
    </Section>
  );
});
DealerSearchFilter.displayName = 'SectionDealerSearchFilter';
